import type { Priority } from "@app/core";
import {
  categoricalPalette,
  CHROMA_BOOST_MAX,
  CHROMA_BOOST_MIN,
  findPreset,
  normalizePresetConfig,
  presetConfig,
  type BoardSample,
  type EntityColorOverrides,
  type RoleName,
  type SchemeVariant,
  type ThemeConfig,
  type ThemeMode,
} from "@app/core";
import { create } from "zustand";
import {
  loadThemeSyncEnabled,
  savePresetConfirmed,
  saveThemeBaseVersion,
  saveThemeSyncEnabled,
} from "../features/theme/theme-metadata.js";
import { getServices } from "../platform.js";
import { useSettingsStore } from "./settings.store.js";

/** Stable, well-known id so every device writes the SAME theme row — the synced
 *  copies converge on one record (unlike calendars/topics, which seed per-device
 *  ids and dedup by name). The theme is a per-user singleton. */
export const ACTIVE_THEME_ID = "theme_active_v1";

/** Which entity scope a color override targets (the specificity hierarchy). */
export type EntityTarget =
  | { kind: "global" }
  | { kind: "taskKind"; isEvent: boolean }
  | { kind: "priority"; priority: Priority }
  | { kind: "calendar"; id: string }
  | { kind: "topic"; id: string };

interface ThemeState {
  loaded: boolean;
  config: ThemeConfig;
  syncEnabled: boolean;
  hydrate: () => Promise<void>;
  setPreset: (presetId: string) => void;
  setSource: (hex: string, mode?: ThemeMode) => void;
  /** Reset a source seed to the active preset's baseline (dark → the preset's
   *  own dark seed, or null to inherit the light seed). Powers the seed's
   *  revert-to-default affordance without leaving the current preset. */
  revertSource: (mode: ThemeMode) => void;
  setVariant: (variant: SchemeVariant) => void;
  setRoleOverride: (mode: ThemeMode, role: RoleName, hex: string | null) => void;
  setPaletteSlot: (index: number, hex: string | null) => void;
  /** Regenerate the whole entity palette from a hue wheel around `hex` (or the
   *  current chrome source) — the optional "generate from a color" affordance for
   *  fully custom themes. Clears per-slot overrides. */
  regeneratePalette: (hex?: string) => void;
  setEntityColor: (target: EntityTarget, hex: string | null) => void;
  /** Record (or clear) where on the palette board a target's color was sampled. */
  setBoardSample: (key: string, sample: BoardSample | null) => void;
  setChromaBoost: (boost: number) => void;
  setAutoColorCalendars: (on: boolean) => void;
  setBlockTintDiff: (diff: number) => void;
  setBlockGradient: (on: boolean) => void;
  revertAll: () => void;
  setSyncEnabled: (on: boolean) => Promise<void>;
  /** Adopt a theme pulled from another device (no sync nudge; resolver owns base). */
  applyRemoteTheme: (theme: ThemeConfig) => Promise<void>;
}

let inflight: Promise<void> | null = null;
let persistTimer: ReturnType<typeof setTimeout> | null = null;

export const useThemeStore = create<ThemeState>((set, get) => ({
  loaded: false,
  config: presetConfig("default", { id: ACTIVE_THEME_ID }),
  syncEnabled: true,

  hydrate() {
    // Idempotent: once loaded, re-reading the repo would clobber unpersisted
    // in-memory edits (the persist is debounced) — reconcileThemeSync calls this
    // defensively, so it must be a no-op after the first load.
    if (get().loaded) return Promise.resolve();
    if (inflight) return inflight;
    inflight = hydrateOnce(set).finally(() => { inflight = null; });
    return inflight;
  },

  setPreset(presetId) {
    const prev = get().config;
    commit(set, get, presetConfig(presetId, { id: prev.id, created_at: prev.created_at }));
    // The user has now actively chosen a theme — future visits to the theme
    // settings start with the preset picker collapsed.
    void savePresetConfirmed();
    // A theme is an aesthetic: adopt its natural light/dark mode so the switch
    // reads as a whole new vibe (Shell mirrors the appearance setting into the
    // ThemeProvider). Leaves "system" users on an explicit mode after a pick.
    void useSettingsStore.getState().setTheme(findPreset(presetId).defaultMode);
  },

  setSource(hex, mode = "light") {
    // Source edits affect CHROME ONLY and KEEP the current preset — the seed
    // gets a revert-to-default affordance (see `revertSource`) instead of
    // flipping to "custom". The curated entity palette persists across chrome
    // tweaks (use `regeneratePalette` to rebuild it from a hue).
    mutate(set, get, (c) =>
      mode === "dark" ? { ...c, sourceDark: hex } : { ...c, source: hex },
    );
  },

  revertSource(mode) {
    const preset = findPreset(get().config.preset);
    mutate(set, get, (c) =>
      mode === "dark" ? { ...c, sourceDark: preset.sourceDark ?? null } : { ...c, source: preset.source },
    );
  },

  setVariant(variant) {
    mutate(set, get, (c) => ({ ...c, variant, preset: "custom" }));
  },

  regeneratePalette(hex) {
    mutate(set, get, (c) => ({
      ...c,
      entityPalette: categoricalPalette(hex ?? c.source, c.variant),
      paletteOverrides: {},
    }));
  },

  setRoleOverride(mode, role, hex) {
    mutate(set, get, (c) => {
      const next = { ...c.roleOverrides[mode] };
      if (hex) next[role] = hex; else delete next[role];
      return { ...c, roleOverrides: { ...c.roleOverrides, [mode]: next } };
    });
  },

  setPaletteSlot(index, hex) {
    mutate(set, get, (c) => {
      const next = { ...c.paletteOverrides };
      if (hex) next[index] = hex; else delete next[index];
      return { ...c, paletteOverrides: next };
    });
  },

  setEntityColor(target, hex) {
    mutate(set, get, (c) => ({ ...c, entity: applyEntityColor(c.entity, target, hex) }));
  },

  setBoardSample(key, sample) {
    mutate(set, get, (c) => {
      const samples = { ...(c.entity.boardSamples ?? {}) };
      if (sample) samples[key] = sample; else delete samples[key];
      const entity = { ...c.entity };
      if (Object.keys(samples).length) entity.boardSamples = samples; else delete entity.boardSamples;
      return { ...c, entity };
    });
  },

  setChromaBoost(boost) {
    const clamped = Math.max(CHROMA_BOOST_MIN, Math.min(CHROMA_BOOST_MAX, Math.round(boost)));
    mutate(set, get, (c) => ({ ...c, chromaBoost: clamped }));
  },

  setAutoColorCalendars(on) {
    mutate(set, get, (c) => ({ ...c, autoColorCalendars: on }));
  },

  setBlockTintDiff(diff) {
    mutate(set, get, (c) => ({ ...c, blockTintDiff: diff }));
  },

  setBlockGradient(on) {
    mutate(set, get, (c) => ({ ...c, blockGradient: on }));
  },

  revertAll() {
    const prev = get().config;
    commit(set, get, presetConfig(prev.preset, { id: prev.id, created_at: prev.created_at }));
  },

  async setSyncEnabled(on) {
    await saveThemeSyncEnabled(on);
    set({ syncEnabled: on });
  },

  async applyRemoteTheme(theme) {
    if (persistTimer) { clearTimeout(persistTimer); persistTimer = null; }
    const config = normalizePresetConfig({ ...theme, id: ACTIVE_THEME_ID });
    set({ config });
    await getServices().themes.update(config).catch(() => getServices().themes.insert(config));
  },
}));

/** Apply a producer immutably, stamp `updated_at`, repaint now, persist debounced. */
function mutate(
  set: (p: Partial<ThemeState>) => void,
  get: () => ThemeState,
  producer: (c: ThemeConfig) => ThemeConfig,
): void {
  const next = { ...producer(get().config), updated_at: new Date().toISOString() };
  commit(set, get, next);
}

function commit(set: (p: Partial<ThemeState>) => void, _get: () => ThemeState, config: ThemeConfig): void {
  set({ config: { ...config, updated_at: config.updated_at || new Date().toISOString() } });
  schedulePersist();
}

function schedulePersist(): void {
  if (persistTimer) clearTimeout(persistTimer);
  persistTimer = setTimeout(() => {
    persistTimer = null;
    void persistNow();
  }, 500);
}

async function persistNow(): Promise<void> {
  const config = useThemeStore.getState().config;
  await getServices().themes.update(config).catch(() => getServices().themes.insert(config));
  // Nudge a (silent) sync so the theme propagates promptly; busy-guarded, no-op
  // when signed out. Lazy import avoids a load-time cycle with sync.store.
  try {
    const { useSyncStore } = await import("./sync.store.js");
    const { useAuthStore } = await import("./auth.store.js");
    if (useAuthStore.getState().accessToken) void useSyncStore.getState().syncNow({ silent: true });
  } catch { /* sync optional */ }
}

function applyEntityColor(entity: EntityColorOverrides, target: EntityTarget, hex: string | null): EntityColorOverrides {
  const next: EntityColorOverrides = { ...entity };
  switch (target.kind) {
    case "global":
      if (hex) next.global = hex; else delete next.global;
      break;
    case "taskKind": {
      const k = { ...(entity.kind ?? {}) };
      const key = target.isEvent ? "event" : "task";
      if (hex) k[key] = hex; else delete k[key];
      if (Object.keys(k).length) next.kind = k; else delete next.kind;
      break;
    }
    case "priority": {
      const p = { ...(entity.priority ?? {}) };
      if (hex) p[target.priority] = hex; else delete p[target.priority];
      if (Object.keys(p).length) next.priority = p; else delete next.priority;
      break;
    }
    case "calendar": {
      const cals = { ...(entity.calendars ?? {}) };
      if (hex) cals[target.id] = hex; else delete cals[target.id];
      if (Object.keys(cals).length) next.calendars = cals; else delete next.calendars;
      break;
    }
    case "topic": {
      const tops = { ...(entity.topics ?? {}) };
      if (hex) tops[target.id] = hex; else delete tops[target.id];
      if (Object.keys(tops).length) next.topics = tops; else delete next.topics;
      break;
    }
  }
  return next;
}

async function hydrateOnce(set: (p: Partial<ThemeState>) => void): Promise<void> {
  const { themes } = getServices();
  const syncEnabled = await loadThemeSyncEnabled();
  const existing = (await themes.listActive()).find((t) => t.id === ACTIVE_THEME_ID)
    ?? (await themes.listActive())[0];
  if (existing) {
    const config = normalizePresetConfig(existing);
    if (config !== existing) await themes.update(config).catch(() => themes.insert(config));
    set({ config, syncEnabled, loaded: true });
    return;
  }
  // First run: build the default theme + fold in existing per-entity colors and
  // the old wash settings, then persist. Base = its updated_at so a pristine
  // device isn't treated as "locally edited" on the first sync.
  const config = await buildMigratedDefault();
  await themes.insert(config);
  await saveThemeBaseVersion(config.updated_at);
  set({ config, syncEnabled, loaded: true });
}

async function buildMigratedDefault(): Promise<ThemeConfig> {
  const base = presetConfig("default", { id: ACTIVE_THEME_ID });
  const { topics, calendars } = getServices();
  const topicColors: Record<string, string> = {};
  for (const t of await topics.listActive()) if (t.color) topicColors[t.id] = t.color;
  const calColors: Record<string, string> = {};
  for (const c of await calendars.listActive()) if (c.color) calColors[c.id] = c.color;
  const entity: EntityColorOverrides = {};
  if (Object.keys(topicColors).length) entity.topics = topicColors;
  if (Object.keys(calColors).length) entity.calendars = calColors;
  const settings = useSettingsStore.getState();
  return {
    ...base,
    entity,
    blockTintDiff: settings.loaded ? settings.blockTintDiff : base.blockTintDiff,
    blockGradient: settings.loaded ? settings.blockGradient : base.blockGradient,
  };
}
